import { useState } from 'react';

import { signIn } from '../lib/brain';

/**
 * The way in: a username and a password, both set on the Worker.
 *
 * There used to be rather more here - a magic link, a password-setup card, a cooldown on
 * resending, an error read out of the URL, and a list of explanations for the several ways
 * Supabase's email flows can fail. All of it is gone, because none of it was the product: the
 * pair is a static `DASHBOARD_USERNAME` / `DASHBOARD_PASSWORD` on the deployed Worker (see
 * `workers/brain/src/auth/login.ts`), and this form posts it there. A wrong pair is answered
 * by a string comparison on the Worker, so there is no email to arrive, no redirect URL to
 * configure and no account state to be wrong.
 *
 * Nothing navigates on success: App.tsx owns the session and swaps this screen for the
 * dashboard the moment `signIn` stores it, so a failure has to clear `busy` itself.
 */
interface Props {
  /** The dev auto sign-in was tried and did not work. See App.tsx. */
  devSignInError?: string | null;
}

export default function Login({ devSignInError }: Props) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function submit() {
    setBusy(true);
    setError('');
    const result = await signIn(username.trim(), password);
    setBusy(false);
    if (!result.ok) setError(result.error ?? 'The login was refused.');
  }

  return (
    <div className="center">
      <div className="card login">
        <h1>NestMatch Outreach</h1>
        <p className="muted">Review queue for the outreach engine.</p>

        {devSignInError ? (
          <p className="error">
            Local auto sign-in did not work: {devSignInError}. If that mentions the dashboard
            account rather than the username and password, run <code>npm run dev:signin</code> to
            repair it. The form below takes the same pair either way.
          </p>
        ) : null}

        <form
          onSubmit={(event) => {
            event.preventDefault();
            void submit();
          }}
        >
          <label htmlFor="username">Username</label>
          <input
            id="username"
            type="text"
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            autoComplete="username"
            autoFocus
            required
          />
          <label htmlFor="password">Password</label>
          <input
            id="password"
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="current-password"
            required
          />
          <button type="submit" disabled={busy}>
            {busy ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        {error ? <p className="error">{error}</p> : null}
      </div>
    </div>
  );
}
